import { useState } from 'react';
import { Info } from 'lucide-react';
import { Button } from '../../components/common/Button';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import { CapabilityModal } from '../../components/admin/CapabilityModal';
import {
  useCapabilities,
  useCreateCapability,
  useUpdateCapability,
  useDeleteCapability,
} from '../../hooks/useCapabilities';
import { useToast } from '../../contexts/ToastContext';
import type { Capability, CapabilityType, SeverityLevel } from '../../types';

const severityColors: Record<SeverityLevel, string> = {
  WARNING: 'bg-yellow-500/20 text-yellow-400',
  BLOCKER: 'bg-red-500/20 text-red-400',
};

const typeLabels: Record<CapabilityType, string> = {
  TECHNICAL: 'Technical',
  BUSINESS: 'Business',
  PERSONNEL: 'Personnel',
};

function Tooltip({ text }: { text: string }) {
  return (
    <div className="group relative inline-block ml-2">
      <Info className="w-4 h-4 text-wolf-500 cursor-help" />
      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-wolf-900 border border-wolf-600 rounded text-xs text-wolf-200 w-72 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
        {text}
      </div>
    </div>
  );
}

export function CapabilitiesPage() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCapability, setEditingCapability] = useState<Capability | null>(null);
  const { showToast } = useToast();

  const { data: capabilities, isLoading, error } = useCapabilities();
  const createMutation = useCreateCapability();
  const updateMutation = useUpdateCapability();
  const deleteMutation = useDeleteCapability();

  const handleOpenCreate = () => {
    setEditingCapability(null);
    setIsModalOpen(true);
  };

  const handleOpenEdit = (capability: Capability) => {
    setEditingCapability(capability);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingCapability(null);
  };

  const handleSave = async (data: {
    name: string;
    description: string;
    type: CapabilityType;
    severity_if_missing: SeverityLevel;
  }) => {
    try {
      if (editingCapability) {
        await updateMutation.mutateAsync({ id: editingCapability.id, data });
        showToast('Capability updated successfully', 'success');
      } else {
        await createMutation.mutateAsync(data);
        showToast('Capability created successfully', 'success');
      }
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Failed to save capability', 'error');
      throw err;
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteMutation.mutateAsync(id);
      showToast('Capability deleted successfully', 'success');
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Failed to delete capability', 'error');
      throw err;
    }
  };

  const isMutating = createMutation.isPending || updateMutation.isPending || deleteMutation.isPending;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner message="Loading capabilities..." />
      </div>
    );
  }

  if (error) {
    return <div className="text-red-400 p-4">Failed to load capabilities</div>;
  }

  // Group capabilities by type, manual first within each group
  const sortedCapabilities = [...(capabilities || [])].sort((a, b) => {
    const aIsRagnarok = a.source === 'ragnarok' ? 1 : 0;
    const bIsRagnarok = b.source === 'ragnarok' ? 1 : 0;
    return aIsRagnarok - bIsRagnarok;
  });

  const grouped = {
    TECHNICAL: sortedCapabilities.filter((c) => c.type === 'TECHNICAL'),
    BUSINESS: sortedCapabilities.filter((c) => c.type === 'BUSINESS'),
    PERSONNEL: sortedCapabilities.filter((c) => c.type === 'PERSONNEL'),
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center">
          <h1 className="text-2xl font-bold text-white">Capabilities</h1>
          <Tooltip text="Capabilities describe what your organization can do. The AI uses these when evaluating projects to check feasibility and identify gaps." />
        </div>
        <div className="flex items-center gap-3">
          <Button onClick={handleOpenCreate}>Add Capability</Button>
        </div>
      </div>

      {capabilities?.length === 0 ? (
        <div className="bg-wolf-800 rounded-lg border border-wolf-700 p-8 text-center">
          <p className="text-wolf-400 mb-4">No capabilities defined yet.</p>
          <p className="text-wolf-500 text-sm">
            Add capabilities to help the AI evaluate projects against your organization's strengths.
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {(['TECHNICAL', 'BUSINESS', 'PERSONNEL'] as const).map((type) => {
            const items = grouped[type];
            if (items.length === 0) return null;

            return (
              <div key={type} className="bg-wolf-800 rounded-lg border border-wolf-700 overflow-hidden">
                <div className="px-4 py-3 bg-wolf-900 border-b border-wolf-700">
                  <h2 className="text-lg font-semibold text-white">{typeLabels[type]}</h2>
                </div>
                <div className="divide-y divide-wolf-700">
                  {items.map((capability) => (
                    <div
                      key={capability.id}
                      className="px-4 py-3 flex items-center justify-between hover:bg-wolf-700/30"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <span className="text-white font-medium">{capability.name}</span>
                          <span
                            className={`px-2 py-0.5 rounded text-xs ${severityColors[capability.severity_if_missing]}`}
                          >
                            {capability.severity_if_missing}
                          </span>
                          {capability.source === 'ragnarok' && (
                            <span className="px-2 py-0.5 rounded text-xs bg-indigo-500/20 text-indigo-400">
                              Ragnarok
                            </span>
                          )}
                        </div>
                        {capability.description && (
                          <p className="text-wolf-400 text-sm mt-1">{capability.description}</p>
                        )}
                      </div>
                      {capability.source !== 'ragnarok' && (
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => handleOpenEdit(capability)}
                        >
                          Edit
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <CapabilityModal
        capability={editingCapability}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSave}
        onDelete={handleDelete}
        isLoading={isMutating}
      />
    </div>
  );
}
